import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Box
} from '@mui/material';
import { SubscriptionBillingService } from '../../services/subscriptionBilling.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { ISubscriptionInvoice, PaymentMethod } from '../../interfaces/billing.interface';

interface Props {
    open: boolean;
    invoice: ISubscriptionInvoice | null;
    onClose: () => void;
    onSuccess: () => void;
}

const METHODS: PaymentMethod[] = ['BANK_TRANSFER', 'BKASH_MANUAL', 'CASH'];

const MarkPaidDialog: React.FC<Props> = ({ open, invoice, onClose, onSuccess }) => {
    const { showSuccess, showApiError } = useToast();
    const { t } = useTranslation('billing');
    const [submitting, setSubmitting] = useState(false);

    const [method, setMethod] = useState<PaymentMethod>('BANK_TRANSFER');
    const [reference, setReference] = useState('');
    const [paidAt, setPaidAt] = useState('');

    useEffect(() => {
        if (open) {
            setMethod('BANK_TRANSFER');
            setReference('');
            setPaidAt('');
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!invoice) return;
        setSubmitting(true);
        try {
            await SubscriptionBillingService.markPaid(invoice.id, {
                method,
                payerReference: reference || undefined,
                // The date input yields "YYYY-MM-DD"; the API expects a full ISO instant.
                paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
            });
            showSuccess(t('markPaidDialog.marked'));
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, t('markPaidDialog.markFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('markPaidDialog.title')}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <FormControl fullWidth required>
                        <InputLabel id="mark-paid-method-label">{t('markPaidDialog.method')}</InputLabel>
                        <Select
                            labelId="mark-paid-method-label"
                            label={t('markPaidDialog.method')}
                            value={method}
                            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                            inputProps={{ 'aria-label': t('markPaidDialog.method') }}
                        >
                            {METHODS.map(m => <MenuItem key={m} value={m}>{t(`paymentMethod.${m}`)}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <TextField
                        label={t('markPaidDialog.reference')}
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        fullWidth
                    />

                    <TextField
                        label={t('markPaidDialog.paidAt')}
                        type="date"
                        value={paidAt}
                        onChange={(e) => setPaidAt(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('markPaidDialog.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                    {submitting ? t('markPaidDialog.saving') : t('markPaidDialog.submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarkPaidDialog;
